<template>
  <div class="max-w-6xl mx-auto my-10 px-6 py-8 bg-white dark:bg-gray-800 rounded-lg shadow">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">New Support Request</h1>
    <CustomerBroadcastBanner />

    <div v-if="isAdmin" class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">
        Select Requester or Add New Requester
      </label>
      <select v-model="contact_id" @change="handleContactChange"
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700">
        <option :value="null" disabled>Select a Requester...</option>
        <option value="__add__">Add New Requester</option>
        <option v-for="requester in requesters" :key="requester.id" :value="requester.id">
          {{ requester.name_first }} {{ requester.name_last }}{{ requester.email ? ` - ${requester.email}` : '' }}
        </option>
      </select>
    </div>

    <div class="mb-4" v-if="requesters.length > 0 && isAdmin">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Want others to receive updates on this
        request?</label>
      <div v-click-outside="closeWatcherDropdown">
        <input type="email"
          class="mt-1 block w-full rounded-md border dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700 dark:placeholder-gray-400"
          placeholder="Type to search the user..." @focus="toggleWatcherDropdown" v-model="watchersInput" />
        <ul v-if="showWatcherDropdown"
          class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-600 dark:border-gray-700 dark:text-white scrollable-requester-list">
          <li v-for="watcher in filteredWatchers" :key="watcher.id" @click="addWatcher(watcher)"
            class="cursor-pointer p-3 hover:bg-gray-100 dark:hover:bg-gray-800">
            {{ watcher.name }}
          </li>
        </ul>
        <div class="mt-1 grid grid-cols-7 sm:grid-cols-1 md:grid-cols-7 gap-1">
          <div v-for="watcher in watchersList.filter(w => w.visible)" :key="watcher.id"
            class="flex justify-between items-center border border-gray-200 rounded-full p-1 text-xs dark:border-gray-900 dark:bg-gray-400">
            {{ watcher.name }}
            <button @click="removeWatcher(watcher)"
              class="ml-3 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full text-xs">
              &times;
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Subject -->
    <!-- <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Subject</label>
      <input v-model="subject" type="text" @blur="handleSubjectBlur" class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white shadow-sm border-gray-300 dark:border-gray-700" placeholder="Brief subject of your issue" />
    </div> -->

    <!-- Description -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Describe your issue</label>
      <textarea v-model="description"
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white shadow-sm border-gray-300 dark:border-gray-700"
        rows="5" placeholder="Please describe the problem in detail..."></textarea>
      <p v-if="formErrors.description" class="text-red-600 text-sm mt-1">{{ formErrors.description }}</p>
    </div>

    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">PRIVATE: Use space to provide passwords
        or private information related to issue (transmitted securely):</label>
      <textarea v-model="private"
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white shadow-sm border-gray-300 dark:border-gray-700"
        rows="5" placeholder="Include your private info..."></textarea>
    </div>

    <!-- Type (Auto-Populated) -->
    <!-- <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 flex items-center justify-between">
        Auto-detected topic (Please select topic that best fits your request if incorrect)
        <button
          type="button"
          class="text-xs text-blue-600 dark:text-blue-300 hover:underline"
          @click="showTypeOptions = !showTypeOptions"
        >
          {{ showTypeOptions ? 'Hide' : 'Change' }} Type
        </button>
      </label>
      <input
        v-model="autoType"
        @click="showTypeOptions = !showTypeOptions"
        type="text"
        disabled
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white shadow-sm border-gray-300 dark:border-gray-700"
      />
    </div> -->

    <div class="mb-4" v-if="topTypeMatches.length">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">
        Auto-detected topic (Please select topic that best fits your request if incorrect)
      </label>
      <select v-model="selectedTypeId"
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700">
        <option v-for="type in topTypeMatches" :key="type.id" :value="type.id">
          {{ type.description }}
        </option>
      </select>
      <p v-if="formErrors.selectedTypeId" class="text-red-600 text-sm mt-1">{{ formErrors.selectedTypeId }}</p>
    </div>

    <!-- Manual Type Selection -->
    <!-- <div v-if="showTypeOptions" class="mb-6">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">Not right? Choose your issue type:</label>
      <div class="flex gap-2 flex-wrap border-b border-gray-300 dark:border-gray-600 mb-4">
        <button
          v-for="(group, category) in categorizedTypes"
          :key="category"
          @click="activeTab = category"
          class="px-4 py-1 text-sm font-medium border-b-2"
          :class="{
            'border-yellow-400 text-yellow-600 dark:text-yellow-300': activeTab === category,
            'border-transparent text-gray-600 dark:text-gray-300 hover:text-yellow-600': activeTab !== category
          }"
        >
          {{ category }}
        </button>
      </div>

      <div v-for="(group, category) in categorizedTypes" :key="category" v-show="activeTab === category">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="type in group"
            :key="type.id"
            @click="selectType(type)"
            class="px-3 py-1 text-sm rounded-full border dark:border-gray-600 border-gray-300 
              bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-yellow-400 dark:hover:bg-yellow-500 
              transition-all duration-150"
            :class="{ 'bg-yellow-300 dark:bg-yellow-600 font-bold': selectedTypeId === type.id }"
          >
            {{ type.description }}
          </button>
        </div>
      </div>
    </div> -->

    <!-- <div v-if="printers.length > 0" class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">
        Select Printer
      </label>
      <select v-model="selectedPrinterId" class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700">
        <option :value="null" disabled>Select a printer...</option>
        <option v-for="printer in printers" :key="printer.id" :value="printer.id">
          {{ printer.department }} - {{ printer.printer_model.manufacturer }} {{ printer.printer_model.model }}
        </option>
      </select>
    </div> -->

    <!-- Printer Supplies -->
    <!-- <div v-if="selectedTypeId == 33 && selectedPrinterId && printerSupplies.length > 0" class="mb-6">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">Select Printer Supplies:</label>
      <div class="space-y-4">
        <div v-for="(item, index) in printerSupplies" :key="item.cid" class="flex items-center justify-between gap-4">
          <div class="flex-1">
            <div class="font-medium dark:text-white">{{ item.hw_type }}: {{ item.description }}</div>
            <div class="text-sm text-gray-500 dark:text-gray-300">
              ${{ item.cost }} &nbsp;
              <span v-if="item.pctsaved > 0" class="text-green-500">(Save {{ item.pctsaved }}%)</span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-sm dark:text-white">Qty:</label>
            <input
              v-model.number="item.amount"
              type="number"
              min="0"
              class="w-16 dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700 rounded-md"
            />
            <select v-model="item.action" class="dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700 rounded-md">
              <option value="install">Install</option>
              <option value="drop off">Drop Off</option>
            </select>
          </div>
        </div>
      </div>
    </div> -->



    <!-- File Upload -->
    <div class="mb-4">
      <FileUploadList v-model="uploadedFiles" label="Attach Files" />
    </div>

    <!-- SLA Priority -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">
        When would you like this handled?
      </label>
      <p class="text-xs text-gray-600 dark:text-gray-400 mb-1">
        For details on response expectations, please refer to our
        <a href="https://support.aimitservices.com/attention-protocol.html" target="_blank" rel="noopener noreferrer"
          class="text-blue-600 dark:text-blue-300 underline">
          Service Level Agreement (SLA)
        </a>.
      </p>
      <select v-model="selectedSlaOption"
        class="mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-white border-gray-300 dark:border-gray-700">
        <option value="null" selected disabled>Select Timeframe</option>
        <option v-for="type in timeFrames" :key="type.id" :value="type.id">{{ type.label }}</option>
      </select>
      <p v-if="formErrors.selectedSlaOption" class="text-red-600 text-sm mt-1">{{ formErrors.selectedSlaOption }}</p>
    </div>

    <!-- Email Updates Checkbox -->
    <div class="mb-4 flex items-center">
      <input type="checkbox" id="sendUpdates" v-model="sendEmailUpdates" class="mr-2" />
      <label for="sendUpdates" class="text-sm text-gray-700 dark:text-gray-200">
        Leave checked if you would like to receive confirmations/updates by email.
      </label>
    </div>

    <div class="mb-4">
      <label class="flex items-start gap-2">
        <input type="checkbox" v-model="urgentRequest" @change="handleUrgentChange" class="mt-1" />
        <span class="text-sm text-gray-700 dark:text-gray-200">
          Mark this request as <strong>Urgent</strong><br>
          This request will page our entire support team. If this does not involve a <strong>server outage, network
            failure, or business-critical system issue</strong>, we recommend leaving this box unchecked. Although we do
          our best, obvious delays in contacting technicians after standard business hours may occur.
          <span class="text-red-600 dark:text-red-400 font-semibold">Additional fees for emergency service may be
            incurred</span>
        </span>
      </label>
    </div>


    <!-- Submit Button -->
    <div v-if="phoneRequiredForUser && !requesterHasPhone"
      class="mb-3 p-3 rounded-md border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-500">
      <p class="text-sm text-yellow-900 dark:text-yellow-100">
        Work phone number is required on your profile before you can submit a support request.
        <router-link to="/extprofile?phonerequired=1" class="underline font-semibold">Update your profile</router-link>.
      </p>
    </div>
    <div class="mt-6">
      <button @click="submitTicket" :disabled="phoneRequiredForUser && !requesterHasPhone" :class="[
        'px-6 py-2 rounded-md text-white',
        (phoneRequiredForUser && !requesterHasPhone)
          ? 'bg-gray-400 cursor-not-allowed'
          : 'bg-yellow-500 hover:bg-yellow-400'
      ]">Submit Request</button>
    </div>
    <AddRequesterModal :isVisible="showAddUserModal" @close="showAddUserModal = false"
      @complete="handleAddUserCompletion" />

    <!-- Urgent Confirmation Modal -->
    <div v-if="showUrgentModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md">
        <h2 class="text-lg font-semibold dark:text-gray-100 mb-4">Confirm Urgent Request</h2>
        <p class="text-sm text-gray-700 dark:text-gray-200 mb-3">
          Are you sure this is an <strong>urgent</strong> request involving a <strong>server outage, network failure, or
            business-critical system issue</strong>?
        </p>
        <p class="text-sm text-red-600 dark:text-red-400 font-medium mb-4">
          Checking this box will page our entire staff, and additional fees for emergency service may be
          incurred.
        </p>
        <div class="flex justify-end space-x-2 mt-4">
          <button @click="cancelUrgent"
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100">Cancel</button>
          <button @click="confirmUrgent" class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">Yes,
            mark as urgent</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import AddRequesterModal from "@/views/modals/AddRequesterModal.vue";
import FileUploadList from "@/views/components/FileUploadList.vue";
import CustomerBroadcastBanner from "@/views/components/CustomerBroadcastBanner.vue";


export default {
  components: {
    AddRequesterModal,
    FileUploadList,
    CustomerBroadcastBanner,
  },
  data() {
    return {
      // subject: '',
      userroles: [70],
      description: '',
      lastMatchedDesc: '',
      private: '',
      autoType: '',
      uploadedFiles: [],
      userId: null,
      companyId: null,
      contact_id: null,
      ticketTypes: [],
      watchersInput: '',
      showWatcherDropdown: false,
      requesters: [],
      requesterHasPhone: false,
      printerSupplies: [],
      originalContactId: null,
      watchersList: [],
      topTypeMatches: [],
      selectedTypeId: 4, // Default to E-mail/Outlook (AI type matching disabled)
      showAddUserModal: false,
      showTypeOptions: false,
      activeTab: 'Website/Software Development',
      selectedSlaOption: null,
      priority: '',
      formErrors: {
        description: '',
        selectedTypeId: '',
        selectedSlaOption: '',
      },
      sendEmailUpdates: true,
      urgentRequest: false,
      showUrgentModal: false,
      timeFrames: [],
      timeframesMapping: {
        '1': this.getCurrentLocalTime(), // Immediately (current local time)
        '2': { days: 0, time: '12:00' }, // By noon today
        '3': { days: 0, time: '17:00' }, // By end of day
        '8': { days: this.getNextBusinessDayOffset(), time: '12:00' }, // Noon next business day
        '4': { days: this.getNextBusinessDayOffset(), time: '17:00' }, // End of next business day
        '5': this.getEndOfWeekFriday(), // By end of week (upcoming Friday 5 PM)
        '6': this.getEndOfNextWeekFriday(), // By end of next week (following Friday 5 PM)
        '7': { days: 30, time: '17:00' }, // No rush, whenever you can get to it
      },
      printers: [],
      selectedPrinterId: null,
      aiSuggested: false
    };
  },
  computed: {
    categorizedTypes() {
      const merged = {};
      this.ticketTypes.forEach(type => {
        const cat = type.category === 'Software' ? 'Website/Software Development' : (type.category || 'Uncategorized');
        if (!merged[cat]) merged[cat] = [];
        merged[cat].push(type);
      });
      return merged;
    },
    isAdmin() {
      return this.userroles.includes(60);
    },
    phoneRequiredForUser() {
      return this.userroles.includes(60) || this.userroles.includes(70);
    },
    filteredWatchers() {
      return this.watchersInput
        ? this.requesters.filter((e) =>
          e.name
            .toLowerCase()
            .includes(this.watchersInput.toLowerCase())
        ).slice(0, 20)
        : this.requesters;
    },
  },
  watch: {
    autoType(newVal) {
      this.showTypeOptions = !newVal || newVal === 'Other';
    },

    description(newVal) {
      if (newVal && newVal.trim() !== '') {
        this.formErrors.description = '';
      }
    },
    selectedTypeId(newVal) {
      if (newVal) {
        this.formErrors.selectedTypeId = '';
      }
    },
    'selectedSlaOption': function (newVal) {
      if (newVal) {
        this.formErrors.selectedSlaOption = '';
      }
      if (newVal && this.timeframesMapping[newVal]) {
        const { days, time } = this.timeframesMapping[newVal];
        this.setPromisedDateTime(days, time);
      }
    },
    contact_id(newVal) {
      // If selected contact is not self and not already added as watcher, add it
      if (newVal && newVal !== this.originalContactId) {
        const existing = this.watchersList.find(w => w.id === this.originalContactId);
        const contact = this.requesters.find(r => r.id === this.originalContactId);
        if (!existing && contact) {
          this.watchersList.push({ id: contact.id, name: `${contact.name_first} ${contact.name_last}`, visible: true });
        }
      }
    },
    // selectedTypeId(newVal) {
    //   if (newVal == 33 || newVal == 5) {
    //     this.fetchPrinters();
    //   } else {
    //     this.printers = [];
    //     this.selectedPrinterId = null;
    //   }
    // },
    // selectedPrinterId(newVal) {
    //   if (this.selectedTypeId == 33 && this.selectedPrinterId) {
    //     this.fetchPrinterSupplies(this.selectedPrinterId);
    //   }
    // }
  },
  methods: {
    getNextBusinessDayOffset() {
      const today = new Date()
      const day = today.getDay()

      if (day === 5) return 3
      if (day === 6) return 2
      return 1
    },
    removeWatcher(watcher) {
      const watcherIndex = this.watchersList.findIndex(w => w.id === watcher.id);
      if (watcherIndex !== -1) {
        this.watchersList[watcherIndex].visible = false;
      }
    },
    addWatcher(watcher) {
      if (!this.watchersList.some(w => w.id === watcher.id)) {
        this.watchersList.push({ id: watcher.id, name: watcher.name, visible: true });
      }
      this.showWatcherDropdown = false;
      this.watchersInput = '';
    },
    closeWatcherDropdown() {
      this.showWatcherDropdown = false;
    },
    toggleWatcherDropdown() {
      this.showWatcherDropdown = !this.showWatcherDropdown;
    },
    handleContactChange(event) {
      if (this.contact_id === '__add__') {
        this.contact_id = null;
        this.showAddUserModal = true;
      }
    },
    async fetchPrinters() {
      this.$updateLoading?.(1);
      try {
        const response = await axios.get(`printers/get/${this.companyId}`);
        this.printers = response.data;
      } catch (error) {
        console.error('Failed to fetch printers:', error);
      } finally {
        this.$updateLoading?.(-1);
      }
    },

    async fetchPrinterSupplies(printerId) {
      this.$updateLoading?.(1);
      try {
        const response = await axios.get(`/printers/${printerId}/items`);
        this.printerSupplies = response.data.map(item => ({
          cid: item.cid,
          sku: item.sku,
          description: item.description,
          hw_type: item.HW_type,
          cost: parseFloat(item.cost),
          pctsaved: parseFloat(item.pctsaved),
          amount: 0,
          action: 'install',
        }));
      } catch (err) {
        console.error('Failed to fetch printer items:', err);
      } finally {
        this.$updateLoading?.(-1);
      }
    },
    getEndOfWeekFriday() {
      const today = new Date();
      const currentDay = today.getDay();
      const daysUntilFriday = (5 - currentDay + 7) % 7;
      return { days: daysUntilFriday, time: '17:00' };
    },
    async handleAddUserCompletion({ newUser }) {
      this.$updateLoading?.(1);
      const formData = new FormData();
      formData.append('email', newUser.email);
      formData.append('requester', newUser.firstName + ' ' + newUser.lastName);
      formData.append('phone', newUser.phone);
      formData.append('extension', newUser.extension || '');
      formData.append('company_id', this.companyId);

      try {
        const response = await axios.post('requesters/add', formData);
        // Reset form
        this.requesters = response.data.requesters;
        this.contact_id = response.data.contact_id;
      } catch (err) {
        console.error(err);
        alert('Failed to add requester.');
      } finally {
        this.$updateLoading?.(-1);
      }
      this.showAddUserModal = false;
    },
    getEndOfNextWeekFriday() {
      const today = new Date();
      const currentDay = today.getDay();
      const daysUntilNextFriday = ((5 - currentDay + 7) % 7) + 7;
      return { days: daysUntilNextFriday, time: '17:00' };
    },
    getCurrentLocalTime() {
      const now = new Date();
      return { days: 0, time: now.toTimeString().slice(0, 5) };
    },
    formatForDatetimeLocal(date) {
      const d = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
      const t = date.toLocaleTimeString('en-CA', { hour12: false, hour: '2-digit', minute: '2-digit' }); // HH:mm
      return `${d}T${t}`;
    },
    setPromisedDateTime(daysOffset, time) {
      const promisedDate = new Date();
      promisedDate.setDate(promisedDate.getDate() + daysOffset);

      // Convert to local timezone
      const [hours, minutes] = time.split(':');
      promisedDate.setHours(hours, minutes, 0, 0);

      // Adjust to user's local timezone
      // const localOffset = promisedDate.getTimezoneOffset() * 60000;
      // const localTime = new Date(promisedDate.getTime() - localOffset);
      // const localISOTime = localTime.toISOString().slice(0, 16);
      this.priority = this.formatForDatetimeLocal(promisedDate);
    },
    // handleSubjectBlur() {
    //   if (this.description) {
    //     this.categorizeType();
    //   }
    // },
    handleUrgentChange() {
      if (this.urgentRequest) {
        this.showUrgentModal = true;
      }
    },
    confirmUrgent() {
      this.showUrgentModal = false;
    },
    cancelUrgent() {
      this.urgentRequest = false;
      this.showUrgentModal = false;
    },
    selectType(type) {
      this.selectedTypeId = type.id;
      this.autoType = type.description;
      this.activeTab = type.category === 'Software' ? 'Website/Software Development' : type.category;
    },
    // categorizeType() {
    //   const stopwords = ['to', 'the', 'a', 'an', 'in', 'on', 'for', 'and', 'or', 'of', 'with', 'at', 'by'];
    //   const importantWords = [
    //     // Security & Access
    //     'access', 'security', 'password', 'account', 'drive', 'file', 'remote', 'vpn', 'LMI', 'surveillance', 'backup',
    //     'restore','compliance', 'new', 'employee', 'terminate', 'spam', 'unblock', 'block',

    //     // Helpdesk/User
    //     'printer', 'scanner', 'device', 'tablet', 'mdm', 'laptop', 'pc', 'supplies', 'email', 'outlook', 'hw', 'sw',
    //     'install', 'equipment', 'repair', 'rma', 'phone', 'voip', 'fax', 'virus', 'malware', 'word', 'excel', 'powerpoint',

    //     // Network & Servers
    //     'network', 'server', 'configuration', 'troubleshooting', 'asset', 'audit', 'cable', 'cabling', 'distribution', 'groups', 'mailing', 'troubleshoot', 'configure',

    //     // Enterprise Apps
    //     'atms', 'efish', 'instant', 'messaging', 'quickbooks',

    //     // Web/Software Dev
    //     'google', 'workspace', 'api', 'data', 'exchange', 'bug', 'debug', 'error', 'integrity', 'database', 'host', 'dns', 'ssl',
    //     'hosting', 'change', 'functionality', 'notifications', 'text', 'push', 'project', 'management', 'research', 'seo', 'marketing', 'site',
    //     'layout', 'graphics', 'content',


    //     //Administration
    //     'accounting', 'finance', 'contract', 'agreement', 'contracts', 'agreements', 'hr', 'tax', 'on-boarding', 'boarding', 'board', 'sales', 'appreciate', 'vendor', 'adminstration',


    //     'website', 'wordpress', 'joomla', 'forms', 'interface', 'scripts', 'validation', 'verification', 'backend', 'frontend', 'event', 'registration',
    //     'deployment', 'sync', 'automation', 'design', 'admin', 'dev', 'development'
    //   ];


    //   const text = `${this.description}`.toLowerCase();
    //   const words = text
    //     .split(/[\s\/\-]+/)
    //     .filter(w => w && !stopwords.includes(w));

    //   const scored = this.ticketTypes.map(type => {
    //     const desc = type.description?.toLowerCase() || '';
    //     const category = type.category?.toLowerCase() || '';
    //     const combined = `${desc} ${category}`;
    //     let score = 0;

    //     words.forEach(word => {
    //       const regex = new RegExp(`\\b${word}\\b`);
    //       if (importantWords.includes(word)) {
    //         if (regex.test(desc)) score += 8;
    //         if (regex.test(category)) score += 6;
    //       } else {
    //         if (regex.test(desc)) score += 4;
    //         if (regex.test(category)) score += 3;
    //       }
    //     });

    //     return { ...type, score };
    //   });

    //   const sorted = scored
    //     .filter(t => t.score > 0)
    //     .sort((a, b) => b.score - a.score)
    //     .slice(0, 3);

    //   this.topTypeMatches = sorted;

    //   if (sorted.length > 0) {
    //     this.autoType = sorted[0].description;
    //     this.selectedTypeId = sorted[0].id;
    //   } else {
    //     this.autoType = 'Other';
    //     this.selectedTypeId = 8;
    //   }
    // },
    async submitTicket() {

      this.formErrors = {
        description: '',
        selectedTypeId: '',
        selectedSlaOption: ''
      };

      let isValid = true;

      if (this.phoneRequiredForUser && !this.requesterHasPhone) {
        alert('A phone number is required on your profile before you can submit a support request. Please update your profile first.');
        this.$router.push('/extprofile?phonerequired=1');
        return;
      }

      if (!this.description || this.description.trim() === '') {
        this.formErrors.description = 'Description is required.';
        isValid = false;
      }

      if (!this.selectedTypeId) {
        this.formErrors.selectedTypeId = 'Please select a topic.';
        isValid = false;
      }

      if (!this.selectedSlaOption) {
        this.formErrors.selectedSlaOption = 'Please select when you want this handled.';
        isValid = false;
      }

      if (!isValid) return;

      this.$updateLoading?.(1);
      const formData = new FormData();
      if (this.private) {
        try {
          const response = await axios.post('/encrypt-private', { private: this.private });
          formData.append('private', response.data.encrypted);
        } catch (e) {
          console.error('Encryption failed:', e);
          alert('Failed to encrypt private information.');
          this.$updateLoading?.(-1);
          return;
        }
      }
      if (this.watchersList.length > 0) {
        this.watchersList.forEach(watcher => {
          formData.append('watchers[]', JSON.stringify({ id: watcher.id, visible: watcher.visible }));
        });
      }

      formData.append('description', this.description);
      formData.append('priority', this.priority);
      formData.append('tickettypeid', this.selectedTypeId);
      formData.append('ai_type_matched', this.aiSuggested ? 1 : 0);
      formData.append('emailnotification', this.sendEmailUpdates ? 1 : 0);
      formData.append('urgency', this.urgentRequest ? 1 : 0);
      formData.append('company_id', this.companyId);
      formData.append('user_id', this.userId);
      formData.append('timeframe_id', this.selectedSlaOption);
      // formData.append('printer_id', this.selectedPrinterId || '');
      formData.append('contact_id', this.contact_id || '');

      this.uploadedFiles.forEach((file, index) => {
        formData.append(`docs[${index}]`, file);
      });

      // if (this.selectedTypeId == 33 && this.printerSupplies.length > 0) {
      //   const filteredSupplies = this.printerSupplies.filter(item => item.amount > 0);
      //   filteredSupplies.forEach((item, index) => {
      //     formData.append(`supplies[${index}][cid]`, item.cid);
      //     formData.append(`supplies[${index}][amount]`, item.amount);
      //     formData.append(`supplies[${index}][cost]`, item.cost);
      //     formData.append(`supplies[${index}][hw_type]`, item.hw_type);
      //     formData.append(`supplies[${index}][description]`, item.description);
      //     formData.append(`supplies[${index}][action]`, item.action);
      //   });
      // }

      try {
        const response = await axios.post('ticket/add-support', formData);
        // Reset form
        // this.subject = '';
        this.description = '';
        this.autoType = '';
        this.selectedTypeId = 4;
        this.uploadedFiles = [];
        this.topTypeMatches = [];
        this.private = '';
        this.selectedSlaOption = null;
        // this.printerSupplies = [];
        this.sendEmailUpdates = true;
        this.urgentRequest = false;
        this.showWatcherDropdown = false;
        this.watchersInput = '';
        this.watchersList = [];
        this.$router.push({ name: 'viewClientTickets' });
        // this.selectedPrinterId = null;
      } catch (err) {
        console.error(err);
        alert('Failed to submit ticket.');
      }
      this.$updateLoading?.(-1);
    },
    async fetchCompanyAndTypes() {
      this.$updateLoading?.(1); // optional global loading
      try {
        const [roleResponse, companyRes] = await Promise.all([
          axios.get(`user/role/${this.userId}`),
          axios.get(`user/company/${this.userId}`),
        ]);
        this.userroles = roleResponse.data.roles;

        if (!companyRes.data || companyRes.data.length === 0) {
          console.warn('No company associated with this user.');
          return;
        }
        this.companyId = companyRes.data[0].company_id;

        const isAdmin = this.userroles && (this.userroles.includes(60));
        const calls = [
          axios.get(`ticket/get-types/${this.companyId}`),
          axios.post(`requesters/getuser`, { id: this.userId }),
          axios.get(`ticket/timeframes`),
        ];
        if (isAdmin) {
          calls.push(axios.get(`requesters/`));
        }
        const [typeRes, currentUserAddressbook, timeFramesResponse, requestersResponse] =
          await Promise.all(calls);

        this.ticketTypes = typeRes.data;

        const ab = currentUserAddressbook.data && typeof currentUserAddressbook.data === 'object'
          ? currentUserAddressbook.data
          : {};
        this.contact_id = ab.id;
        this.originalContactId = ab.id;
        this.requesterHasPhone = !!(ab.phone_primary || '').trim();

        this.timeFrames = timeFramesResponse.data;
        const timeframe7 = this.timeFrames.find(t => t.id === 7);
        if (timeframe7) {
          timeframe7.label = 'No rush – anytime within a month is fine';
        }

        if (isAdmin && requestersResponse) {
          this.requesters = requestersResponse.data;
        }
      } catch (error) {
        console.error('Error fetching company or ticket types:', error);
      } finally {
        this.$updateLoading?.(-1);
      }
    }
  },
  mounted() {
    // if (this.timeframesMapping[this.selectedSlaOption]) {
    //   const { days, time } = this.timeframesMapping[this.selectedSlaOption];
    //   this.setPromisedDateTime(days, time);
    // }
    this.userId = localStorage.getItem('user_id');
    if (this.userId) {
      this.fetchCompanyAndTypes();
    }
  }
};
</script>
<style scoped>
.scrollable-requester-list {
  max-height: 400px;
  /* Adjust this value based on your design needs */
  overflow-y: auto;
  /* Enables vertical scrolling */
  border: 1px solid #ccc;
  /* Optional: adds a border around the list */
}
</style>
